// ============================================================================
// Campaign Service for Lambda Functions
// ============================================================================
// This service communicates with the campaign generation Lambda function.
// All processing is done in-memory without persistent storage.
// ============================================================================

import axios from 'axios';

// Configure base URL from environment or default to local
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  timeout: 60000, // 1 minute timeout for campaign generation
  withCredentials: false // Set to false for CORS with '*' origin
});

class CampaignService {
  /**
   * Resize image to maximum dimension while preserving aspect ratio
   */
  async resizeImage(file, maxSize = 512) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Calculate new dimensions
          let width = img.width;
          let height = img.height;
          
          // Only resize if image is larger than maxSize
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = Math.round((height * maxSize) / width);
              width = maxSize;
            } else {
              width = Math.round((width * maxSize) / height);
              height = maxSize;
            }
          }
          
          // Create canvas and resize
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          
          // Use better quality settings
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convert to blob with JPEG format for smaller size
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const resizedFile = new File([blob], file.name, { 
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                console.log(`Campaign image resized from ${img.width}x${img.height} to ${width}x${height}`);
                console.log(`File size: ${(file.size / 1024).toFixed(2)}KB → ${(blob.size / 1024).toFixed(2)}KB`);
                resolve(resizedFile);
              } else {
                reject(new Error('Failed to create blob from canvas'));
              }
            },
            'image/jpeg',
            0.9 // 90% quality for good balance of size and quality
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convert file to base64 string
   */
  async fileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        // Remove data URL prefix to get pure base64
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Detect image format from file
   */
  getImageFormat(file) {
    const type = file.type;
    if (type === 'image/png') return 'image/png';
    if (type === 'image/gif') return 'image/gif';
    if (type === 'image/webp') return 'image/webp';
    return 'image/jpeg'; // Default to JPEG for jpg, jpeg, and unknown types
  }

  /**
   * Generate campaign content using Lambda function
   * This replaces both prefillCampaign and generateCampaigns
   */
  async generateCampaign({ campaignName, startDate, endDate, websiteUrl, imageFile }) {
    try {
      // Resize image first to reduce payload size
      console.log('Resizing campaign image...');
      const resizedFile = await this.resizeImage(imageFile, 512);
      
      // Convert resized image to base64
      const imageBase64 = await this.fileToBase64(resizedFile);
      const imageFormat = 'image/jpeg'; // Always JPEG after resize
      
      // Prepare payload for Lambda function
      const payload = {
        image_bytes: imageBase64,
        image_format: imageFormat,
        campaign_name: campaignName,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        website_url: websiteUrl
      };
      
      // Call Lambda function
      const response = await api.post('/api/campaign/generate', payload);
      
      // Parse response
      let campaignData;
      if (typeof response.data === 'string') {
        campaignData = JSON.parse(response.data);
      } else if (response.data.body) {
        // Handle Lambda proxy response format
        campaignData = JSON.parse(response.data.body);
      } else {
        campaignData = response.data;
      }
      
      // Extract AI reasoning from the campaign data
      const reasoning = campaignData.ai_reasoning || null;
      
      // Return in the format the frontend expects
      return {
        data: campaignData,
        reasoning: reasoning,
        status: 'success'
      };
      
    } catch (error) {
      console.error('Error generating campaign:', error);
      
      // Log detailed error information
      if (error.response?.data) {
        console.error('Server Error:', error.response.data);
        if (error.response.data.error) {
          throw new Error(error.response.data.error);
        }
      }
      
      throw new Error('Failed to generate campaign content');
    }
  }
  
  /**
   * Legacy method for backward compatibility
   * Now just calls generateCampaign
   */
  async prefillCampaign({ campaignName, startDate, endDate, websiteUrl, imageFile }) {
    return this.generateCampaign({ campaignName, startDate, endDate, websiteUrl, imageFile });
  }
  
  /**
   * Legacy method for backward compatibility
   * The actual generation now happens in prefillCampaign/generateCampaign
   */
  async generateCampaigns({ campaignName, startDate, endDate, websiteUrl, imageFile, aiData }) {
    // Since Lambda function does everything in one call,
    // this method just returns the existing data
    if (aiData) {
      return {
        data: aiData,
        status: 'success'
      };
    }
    
    // If no aiData provided, generate new campaign
    return this.generateCampaign({ campaignName, startDate, endDate, websiteUrl, imageFile });
  }
  
  /**
   * Get sample campaign data for testing
   */
  getSampleCampaignData() {
    return {
      google_ads: {
        headlines: [
          "Summer Sale - 50% Off",
          "Limited Time Offer",
          "Shop Now & Save"
        ],
        descriptions: [
          "Discover amazing deals on our entire collection. Shop now!",
          "Don't miss out on our biggest sale of the year. Limited time!"
        ],
        keywords: ["summer sale", "discount", "promotion", "deals", "offers"],
        campaign_type: "Search",
        bidding_strategy: "CPC"
      },
      meta: {
        primary_text: "Discover our amazing summer collection with 50% off everything!",
        headline: "Summer Sale - 50% Off",
        description: "Shop Now",
        call_to_action: "Shop Now",
        interests: ["Online Shopping", "Fashion", "Deals"],
        age_range: "25-54",
        placement: "Facebook Feed, Instagram Feed"
      },
      tiktok: {
        caption: "Summer vibes on sale! 🌞 Get 50% off everything!",
        hashtags: ["#SummerSale", "#Deals", "#Fashion", "#TikTokMadeMeBuyIt", "#Sale"],
        target_age: "18-34",
        content_style: "Entertainment",
        trends: ["Product showcase", "Summer fashion"]
      },
      budget: {
        total: 5000,
        google_ads: 2500,
        meta: 2000,
        tiktok: 500,
        daily_budget: 166
      },
      ai_reasoning: {
        budget_rationale: "Based on a 30-day campaign, $5000 provides adequate reach across all platforms",
        platform_allocation: "Google Ads gets 50% for high-intent traffic, Meta 40% for awareness, TikTok 10% for younger demographics",
        assumptions: [
          "E-commerce focused campaign",
          "National targeting",
          "Conversion optimization goal"
        ],
        success_metrics: [
          "Target CTR: 2-3%",
          "Conversion rate: 2%",
          "ROAS: 3:1"
        ],
        recommendations: [
          "Start with Google Search campaigns",
          "Use Meta for retargeting",
          "Test TikTok for brand awareness"
        ]
      }
    };
  }
}

const campaignService = new CampaignService();
export default campaignService;